import type { CSSProperties } from 'react';
import type { FlagPatternSpec } from '@mondy/shared';

/**
 * Renders a `FlagPatternSpec` from `@mondy/shared` as an SVG.
 *
 * Faithful port of the design handoff's `PatternSvg` in
 * `/tmp/mondy-design/skins/mondy-v2/project/components.jsx` (lines 17-126).
 * All 11 kinds of the discriminated union are covered. The SVG uses
 * `viewBox="0 0 100 100"` and relies on the parent sizing it.
 */
export function FlagPattern({
  spec,
  style,
  opacity = 1,
}: {
  spec: FlagPatternSpec | null | undefined;
  style?: CSSProperties;
  opacity?: number;
}) {
  if (!spec) return null;
  const s: CSSProperties = { width: '100%', height: '100%', ...style, opacity };

  switch (spec.kind) {
    case 'hStripes': {
      const h = 100 / spec.colors.length;
      return (
        <svg viewBox="0 0 100 100" preserveAspectRatio="none" style={s}>
          {spec.colors.map((col, i) => (
            <rect key={i} x="0" y={i * h} width="100" height={h} fill={col} />
          ))}
        </svg>
      );
    }
    case 'vStripes': {
      const w = 100 / spec.colors.length;
      return (
        <svg viewBox="0 0 100 100" preserveAspectRatio="none" style={s}>
          {spec.colors.map((col, i) => (
            <rect key={i} x={i * w} y="0" width={w} height="100" fill={col} />
          ))}
        </svg>
      );
    }
    case 'cross': {
      const L = spec.offsetL * 100;
      const T = spec.offsetT * 100;
      return (
        <svg viewBox="0 0 100 100" preserveAspectRatio="none" style={s}>
          <rect width="100" height="100" fill={spec.bg} />
          <rect x={L - 6} y="0" width="12" height="100" fill={spec.stroke} />
          <rect x="0" y={T - 6} width="100" height="12" fill={spec.stroke} />
        </svg>
      );
    }
    case 'saltire': {
      return (
        <svg viewBox="0 0 100 100" preserveAspectRatio="none" style={s}>
          <rect width="100" height="100" fill={spec.bg} />
          <line x1="0" y1="0" x2="100" y2="100" stroke={spec.stroke} strokeWidth="16" />
          <line x1="100" y1="0" x2="0" y2="100" stroke={spec.stroke} strokeWidth="16" />
        </svg>
      );
    }
    case 'sun': {
      return (
        <svg viewBox="0 0 100 100" preserveAspectRatio="xMidYMid meet" style={s}>
          <rect width="100" height="100" fill={spec.bg} />
          {Array.from({ length: spec.ringCount }).map((_, i) => {
            const a = (i / spec.ringCount) * Math.PI * 2;
            return (
              <line
                key={i}
                x1="50"
                y1="50"
                x2={50 + Math.cos(a) * 45}
                y2={50 + Math.sin(a) * 45}
                stroke={spec.disc}
                strokeWidth="2"
                opacity=".6"
              />
            );
          })}
          <circle cx="50" cy="50" r="18" fill={spec.disc} />
        </svg>
      );
    }
    case 'taegeuk': {
      // Three short parallel bars per corner (8 long, 1.6 wide), rotated to face center.
      const Trigram = ({ cx, cy, angle }: { cx: number; cy: number; angle: number }) => (
        <g transform={`rotate(${angle} ${cx} ${cy})`}>
          <rect x={cx - 4} y={cy - 3.5} width="8" height="1.6" fill={spec.trigram} />
          <rect x={cx - 4} y={cy - 0.8} width="8" height="1.6" fill={spec.trigram} />
          <rect x={cx - 4} y={cy + 1.9} width="8" height="1.6" fill={spec.trigram} />
        </g>
      );
      return (
        <svg viewBox="0 0 100 100" preserveAspectRatio="xMidYMid meet" style={s}>
          <rect width="100" height="100" fill={spec.bg} />
          <circle cx="50" cy="50" r="22" fill={spec.red} />
          <path
            d="M 28,50 A 22,22 0 0 0 72,50 A 11,11 0 0 1 50,50 A 11,11 0 0 0 28,50 Z"
            fill={spec.blue}
          />
          {/* Top-left, top-right, bottom-left, bottom-right corner trigrams, oriented toward center. */}
          <Trigram cx={20} cy={20} angle={45} />
          <Trigram cx={80} cy={20} angle={-45} />
          <Trigram cx={20} cy={80} angle={-45} />
          <Trigram cx={80} cy={80} angle={45} />
        </svg>
      );
    }
    case 'crescent': {
      return (
        <svg viewBox="0 0 100 100" preserveAspectRatio="xMidYMid meet" style={s}>
          <rect width="100" height="100" fill={spec.bg} />
          <circle cx="50" cy="50" r="22" fill={spec.stroke} />
          <circle cx="56" cy="50" r="18" fill={spec.bg} />
          <polygon points="62,50 72,46 68,52 72,58" fill={spec.stroke} />
        </svg>
      );
    }
    case 'blocks': {
      return (
        <svg viewBox="0 0 100 100" preserveAspectRatio="none" style={s}>
          <rect x="0" y="0" width="50" height="50" fill={spec.tl} />
          <rect x="50" y="0" width="50" height="50" fill={spec.tr} />
          <rect x="0" y="50" width="50" height="50" fill={spec.bl} />
          <rect x="50" y="50" width="50" height="50" fill={spec.br} />
        </svg>
      );
    }
    case 'unionJack': {
      return (
        <svg viewBox="0 0 100 100" preserveAspectRatio="none" style={s}>
          <rect width="100" height="100" fill={spec.bg} />
          <path d="M0,0 L100,100 M100,0 L0,100" stroke={spec.cross1} strokeWidth="12" />
          <path d="M50,0 V100 M0,50 H100" stroke={spec.cross1} strokeWidth="16" />
          <path d="M50,0 V100 M0,50 H100" stroke={spec.cross2} strokeWidth="8" />
        </svg>
      );
    }
    case 'centerEmblem': {
      return (
        <svg viewBox="0 0 100 100" preserveAspectRatio="none" style={s}>
          <rect x="0" y="0" width="25" height="100" fill={spec.left} />
          <rect x="25" y="0" width="50" height="100" fill={spec.center} />
          <rect x="75" y="0" width="25" height="100" fill={spec.right} />
          <path
            d="M50,30 L55,42 L66,42 L57,50 L60,62 L50,55 L40,62 L43,50 L34,42 L45,42 Z"
            fill={spec.left}
          />
        </svg>
      );
    }
    case 'diagonal': {
      return (
        <svg viewBox="0 0 100 100" preserveAspectRatio="none" style={s}>
          <polygon points="0,0 100,0 0,100" fill={spec.a} />
          <polygon points="100,0 100,100 0,100" fill={spec.b} />
        </svg>
      );
    }
    case 'checker': {
      const cells: React.ReactNode[] = [];
      for (let y = 0; y < 5; y++) {
        for (let x = 0; x < 5; x++) {
          cells.push(
            <rect
              key={`${x}-${y}`}
              x={x * 20}
              y={y * 20}
              width="20"
              height="20"
              fill={(x + y) % 2 ? spec.a : spec.b}
            />,
          );
        }
      }
      return (
        <svg viewBox="0 0 100 100" preserveAspectRatio="none" style={s}>
          {cells}
        </svg>
      );
    }
    case 'starCenter': {
      return (
        <svg viewBox="0 0 100 100" preserveAspectRatio="none" style={s}>
          <rect width="100" height="100" fill={spec.bg} />
          <path
            d="M50,30 L58,50 L78,50 L62,62 L68,82 L50,70 L32,82 L38,62 L22,50 L42,50 Z"
            fill="none"
            stroke={spec.starColor}
            strokeWidth="2.5"
          />
        </svg>
      );
    }
    default: {
      // TypeScript exhaustiveness check.
      const _never: never = spec;
      void _never;
      return null;
    }
  }
}
